import type { DashboardExtra, NguonTien } from '@qlk/shared';
import { NGUON_TIEN, NGUON_TIEN_LABEL } from '@qlk/shared';
import { Link } from 'react-router-dom';

import { EmptyState, Skeleton } from '../../../ui/index.js';

/**
 * "Kênh theo nguồn tiền": một thanh xếp chồng + chú giải **có số**.
 *
 * Thay ba thẻ số rời: câu hỏi ở đây là "tiền của hệ thống đi mấy đường, đường
 * nào lớn" — một câu hỏi về **tỷ lệ**, nên vẽ bằng một thanh chia ba đoạn thì
 * trả lời được ngay bằng mắt. Chú giải vẫn ghi con số, vì tỷ lệ không thay được
 * "9 kênh".
 */

/** Màu từng đoạn — lấy chữ của chip cùng tone, không thêm bảng màu mới. */
const LOP_DOAN: Record<NguonTien, string> = {
  network: 'bg-info-chu',
  adsense: 'bg-teal-chu',
  'chua-kiem-tien': 'bg-neutral-chu',
};

/** Tab của trang Kênh ứng với từng đường tiền, để bấm vào là xem đúng tập đó. */
const TAB_KENH: Record<NguonTien, string> = {
  network: 'network',
  adsense: 'adsense',
  'chua-kiem-tien': 'unmonetized',
};

export interface KhoiNguonTienProps {
  duLieu: DashboardExtra['theoNguonTien'] | undefined;
  dangTai: boolean;
  loi?: string | null;
}

export function KhoiNguonTien({ duLieu, dangTai, loi }: KhoiNguonTienProps) {
  const tong = duLieu ? NGUON_TIEN.reduce((t, n) => t + duLieu[n], 0) : 0;

  return (
    <section className="rounded-the border border-vien bg-be-mat" data-khoi="nguon-tien">
      <div className="flex items-center justify-between gap-3 border-b border-vien-trong px-5 py-3.5">
        <h2 className="text-tieu-de-khoi font-semibold text-chu">Kênh theo nguồn tiền</h2>
        <Link to="/kenh" className="text-phu font-semibold text-nhan hover:text-nhan-dam">
          Mở danh sách →
        </Link>
      </div>

      <div className="p-5">
        {dangTai ? (
          <Skeleton cao="96px" bo="vua" />
        ) : loi !== null && loi !== undefined ? (
          <EmptyState loi title="Không tải được số kênh theo nguồn tiền" description={loi} />
        ) : tong === 0 ? (
          <EmptyState
            title="Chưa có kênh nào"
            description="Thêm kênh để thấy tiền của hệ thống đi những đường nào."
          />
        ) : (
          <>
            <div
              className="flex h-2.5 overflow-hidden rounded-full bg-neutral-nen"
              role="img"
              aria-label={NGUON_TIEN.map(
                (n) => `${NGUON_TIEN_LABEL[n]}: ${String(duLieu?.[n] ?? 0)} kênh`,
              ).join(', ')}
            >
              {NGUON_TIEN.map((n) => {
                const so = duLieu?.[n] ?? 0;
                if (so === 0) return null;
                return (
                  <span
                    key={n}
                    className={LOP_DOAN[n]}
                    style={{ width: `${String((so / tong) * 100)}%` }}
                  />
                );
              })}
            </div>

            <ul className="mt-4 flex flex-col gap-2">
              {NGUON_TIEN.map((n) => {
                const so = duLieu?.[n] ?? 0;
                return (
                  <li key={n}>
                    <Link
                      to={`/kenh?tab=${TAB_KENH[n]}`}
                      className="flex items-center gap-2.5 rounded-o py-1 text-co-ban hover:text-nhan"
                    >
                      <span
                        className={`inline-block size-2.5 shrink-0 rounded-sm ${LOP_DOAN[n]}`}
                        aria-hidden="true"
                      />
                      <span className="min-w-0 flex-1 truncate text-chu-phu">
                        {NGUON_TIEN_LABEL[n]}
                      </span>
                      <span className="so-lieu font-semibold text-chu">{so}</span>
                      <span className="so-lieu w-12 text-right text-phu text-chu-phu">
                        {Math.round((so / tong) * 100)}%
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}
